/**
 * Cliente de la API de Westive. Mismo origen que el frontend (Workers Assets), así que la
 * cookie de sesión viaja sola: basta `credentials: "same-origin"` (por defecto) en fetch.
 */

export interface Me {
  id: string;
  firstname: string | null;
  lastname: string | null;
  profilePic: string | null;
  lastSyncAt: string | null;
  hasConnections: boolean;
}

export interface Activity {
  id: string;
  provider: string;
  sport_type: string | null;
  name: string | null;
  start_date: string;
  distance_m: number | null;
  moving_time_s: number | null;
  total_elevation_gain: number | null;
  average_heartrate: number | null;
  average_speed: number | null;
  calories: number | null;
}

export interface TrainingLoadPoint {
  date: string;
  load: number;
  ctl: number;
  atl: number;
  tsb: number;
}

export interface TrainingLoad {
  current: { ctl: number; atl: number; tsb: number };
  series: TrainingLoadPoint[];
}

export interface CoachReply {
  threadId: string;
  reply: string;
  source: "ai" | "rules";
  usage: CoachUsage;
}

export interface CoachUsage {
  usedTokens: number;
  limitTokens: number;
  remainingTokens: number;
  resetAt: string;
  estimated: true;
}

export interface TrainingGoal {
  id: string;
  title: string;
  sport: string;
  target_date: string | null;
  priority: "low" | "normal" | "high" | string;
  status: "active" | "completed" | "archived" | string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AthleteDigest {
  text: string;
  source: "ai" | "rules";
  cached: boolean;
  created_at: string;
}

export interface BodyMeasurement {
  id: string;
  date: string;
  weight_kg: number | null;
  body_fat_pct: number | null;
  muscle_kg: number | null;
  chest_cm: number | null;
  waist_cm: number | null;
  hip_cm: number | null;
  arm_cm: number | null;
  thigh_cm: number | null;
  note: string | null;
}

export interface AccessLogEntry {
  id: string;
  actor_firstname: string | null;
  actor_lastname: string | null;
  action: string;
  resource: string;
  status_code: number | null;
  created_at: string;
}

export interface AppNotification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  meta: string | null;
  read_at: string | null;
  created_at: string;
}

/** Lanzado cuando la sesión no es válida (401): la UI lo trata como "no logueado". */
export class UnauthorizedError extends Error {}

export class ApiError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
  ) {
    super(`${path} -> ${status}`);
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new ApiError(path, res.status);
  return (await res.json()) as T;
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

export const api = {
  me: () => getJson<Me>("/api/me"),

  /** Payload completo del dashboard (shape DashboardData) calculado en el backend por usuario. */
  dashboard: () => getJson<import("./lib/types").DashboardData>("/api/me/dashboard"),

  activities: () => getJson<{ count: number; activities: Activity[] }>("/api/me/activities"),

  uploadActivity: async (file: File): Promise<{ ok: boolean; activityId?: string; error?: string }> => {
    const form = new FormData();
    form.set("file", file);
    const res = await fetch("/api/me/activities/upload", {
      method: "POST",
      body: form,
    });
    return (await res.json().catch(() => ({ ok: false }))) as {
      ok: boolean;
      activityId?: string;
      error?: string;
    };
  },

  trainingLoad: () => getJson<TrainingLoad>("/api/me/training-load"),

  coach: async (body: {
    message: string;
    lang?: "es" | "en";
    threadId?: string | null;
  }): Promise<CoachReply> => {
    const res = await fetchWithTimeout(
      "/api/me/coach",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      },
      30000,
    );
    if (!res.ok) throw new ApiError("/api/me/coach", res.status);
    return (await res.json()) as CoachReply;
  },

  coachUsage: () => getJson<{ usage: CoachUsage }>("/api/me/coach/usage"),

  coachGoals: () => getJson<{ goals: TrainingGoal[] }>("/api/me/coach/goals"),

  createCoachGoal: async (body: {
    title: string;
    targetDate?: string | null;
    priority?: "low" | "normal" | "high";
    notes?: string | null;
  }): Promise<{ goal: TrainingGoal }> => {
    const res = await fetch("/api/me/coach/goals", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError("/api/me/coach/goals", res.status);
    return (await res.json()) as { goal: TrainingGoal };
  },

  updateCoachGoal: async (
    id: string,
    body: {
      title?: string;
      targetDate?: string | null;
      priority?: "low" | "normal" | "high";
      status?: "active" | "completed" | "archived";
      notes?: string | null;
    },
  ): Promise<void> => {
    const res = await fetch(`/api/me/coach/goals/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError(`/api/me/coach/goals/${id}`, res.status);
  },

  deleteCoachGoal: async (id: string): Promise<void> => {
    const res = await fetch(`/api/me/coach/goals/${id}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/me/coach/goals/${id}`, res.status);
  },

  coachMemory: (status?: "active" | "pending" | "archived") =>
    getJson<{ memories: import("./lib/types").CoachMemory[] }>(
      `/api/me/coach/memory${status ? `?status=${status}` : ""}`,
    ),

  createCoachMemory: async (body: {
    kind: string;
    content: string;
  }): Promise<{ memory: import("./lib/types").CoachMemory }> => {
    const res = await fetch("/api/me/coach/memory", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError("/api/me/coach/memory", res.status);
    return (await res.json()) as { memory: import("./lib/types").CoachMemory };
  },

  updateCoachMemory: async (
    id: string,
    body: { kind?: string; content?: string; status?: "active" | "pending" | "archived" },
  ): Promise<void> => {
    const res = await fetch(`/api/me/coach/memory/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError(`/api/me/coach/memory/${id}`, res.status);
  },

  deleteCoachMemory: async (id: string): Promise<void> => {
    const res = await fetch(`/api/me/coach/memory/${id}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/me/coach/memory/${id}`, res.status);
  },

  sync: async (): Promise<void> => {
    const res = await fetch("/api/me/sync", { method: "POST" });
    if (!res.ok) throw new ApiError("/api/me/sync", res.status);
  },

  enrichDetails: async (
    order: "recent" | "oldest",
    limit = 15,
  ): Promise<{ processed: number; pending: number }> => {
    const res = await fetch("/api/me/enrich-details", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ order, limit }),
    });
    if (!res.ok) throw new ApiError("/api/me/enrich-details", res.status);
    return (await res.json()) as { processed: number; pending: number };
  },

  backfillHistory: async (
    maxPages = 1,
  ): Promise<{ imported: number; pagesProcessed: number; nextPage: number; done: boolean }> => {
    const res = await fetch("/api/me/backfill-history", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ maxPages }),
    });
    if (!res.ok) throw new ApiError("/api/me/backfill-history", res.status);
    return (await res.json()) as {
      imported: number;
      pagesProcessed: number;
      nextPage: number;
      done: boolean;
    };
  },

  seedHealth: async (days = 120): Promise<{ days: number; intradayDays: number }> => {
    const res = await fetch("/api/me/dev/seed-health", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ days }),
    });
    if (!res.ok) throw new ApiError("/api/me/dev/seed-health", res.status);
    return (await res.json()) as { days: number; intradayDays: number };
  },

  devSeedAthlete: async (opts?: {
    name?: string;
    withHealth?: boolean;
    weeks?: number;
  }): Promise<{ athleteId: string; activities: number; health: number }> => {
    const res = await fetch("/api/me/dev/seed-athlete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(opts ?? {}),
    });
    if (!res.ok) throw new ApiError("/api/me/dev/seed-athlete", res.status);
    return (await res.json()) as { athleteId: string; activities: number; health: number };
  },

  devImpersonate: async (userId: string): Promise<void> => {
    const res = await fetch("/api/me/dev/impersonate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) throw new ApiError("/api/me/dev/impersonate", res.status);
  },

  devStopImpersonate: async (): Promise<void> => {
    const res = await fetch("/api/me/dev/stop-impersonate", { method: "POST" });
    if (!res.ok) throw new ApiError("/api/me/dev/stop-impersonate", res.status);
  },

  devResetDemo: async (): Promise<{ deleted: number }> => {
    const res = await fetch("/api/me/dev/reset-demo", { method: "POST" });
    if (!res.ok) throw new ApiError("/api/me/dev/reset-demo", res.status);
    return (await res.json()) as { deleted: number };
  },

  devDeleteAthlete: async (userId: string): Promise<void> => {
    const res = await fetch("/api/me/dev/delete-athlete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) throw new ApiError("/api/me/dev/delete-athlete", res.status);
  },

  /** Entrenos publicados del atleta en un rango de fechas (YYYY-MM-DD). */
  calendar: (from: string, to: string) =>
    getJson<{ workouts: import("./lib/types").PlannedWorkout[] }>(
      `/api/me/calendar?from=${from}&to=${to}`,
    ),

  /** El atleta marca un entreno publicado como hecho o saltado. */
  updateCalendarWorkout: async (wid: string, status: "done" | "skipped"): Promise<void> => {
    const res = await fetch(`/api/me/calendar/${wid}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new ApiError(`/api/me/calendar/${wid}`, res.status);
  },

  /** Notas compartidas del coach activo del atleta. */
  coachNotes: () =>
    getJson<{ notes: import("./lib/types").CoachNote[] }>("/api/me/coach-notes"),

  /** Guarda las disciplinas minoritarias que el usuario quiere ver como tab (botón +). */
  saveTabPrefs: async (disciplines: string[]): Promise<void> => {
    const res = await fetch("/api/me/tab-prefs", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ disciplines }),
    });
    if (!res.ok) throw new ApiError("/api/me/tab-prefs", res.status);
  },

  deleteAccount: async (): Promise<void> => {
    const res = await fetch("/api/me", { method: "DELETE" });
    if (!res.ok) throw new ApiError("/api/me", res.status);
  },

  /** Obtiene el coach vinculado del atleta (si tiene). */
  coachLink: () =>
    getJson<{ coach: import("./lib/types").CoachInfo | null }>("/api/me/coach-link"),

  /** Previsualiza un código CCH: devuelve el nombre del coach para la pantalla de consentimiento. */
  previewCoachCode: (code: string) =>
    getJson<{ coachName: string; coachId: string }>(`/api/me/coach-link/preview?code=${encodeURIComponent(code)}`),

  /** Atleta existente canjea un código de coach con consentimiento. */
  redeemCoachCode: async (code: string): Promise<{ ok: boolean; error?: string }> => {
    const res = await fetch("/api/me/coach-link/redeem", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code, consent: true }),
    });
    const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
    return { ok: res.ok && body.ok !== false, error: body.error };
  },

  /** El atleta revoca su vínculo con el coach. */
  revokeCoachLink: async (): Promise<void> => {
    const res = await fetch("/api/me/coach-link", { method: "DELETE" });
    if (!res.ok) throw new ApiError("/api/me/coach-link", res.status);
  },

  /** Mensajes del hilo atleta↔coach (también marca como leídos los del coach). */
  coachMessages: () =>
    getJson<{ messages: import("./lib/types").DirectMessage[] }>("/api/me/coach-messages"),

  /** Nº de mensajes no leídos del coach (para badge). */
  coachMessagesUnread: () =>
    getJson<{ unread: number }>("/api/me/coach-messages/unread"),

  notifications: () =>
    getJson<{ notifications: AppNotification[] }>("/api/me/notifications"),

  notificationsUnread: () =>
    getJson<{ unread: number }>("/api/me/notifications/unread"),

  markNotificationsRead: async (ids?: string[]): Promise<void> => {
    const res = await fetch("/api/me/notifications/read", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(ids ? { ids } : {}),
    });
    if (!res.ok) throw new ApiError("/api/me/notifications/read", res.status);
  },

  accessLog: () =>
    getJson<{ entries: AccessLogEntry[] }>("/api/me/access-log"),

  athleteDigest: (athleteId: string, lang: "es" | "en" = "es", refresh = false): Promise<AthleteDigest> =>
    getJson<AthleteDigest>(`/api/coach/athletes/${encodeURIComponent(athleteId)}/digest?lang=${lang}${refresh ? "&refresh=1" : ""}`),

  body: () =>
    getJson<{ body: BodyMeasurement[] }>("/api/me/body"),

  saveBody: (m: Partial<BodyMeasurement> & { date: string }): Promise<{ body: BodyMeasurement }> =>
    fetch("/api/me/body", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(m),
    }).then(async (r) => {
      if (!r.ok) throw new ApiError("/api/me/body", r.status);
      return r.json() as Promise<{ body: BodyMeasurement }>;
    }),

  deleteBody: async (id: string): Promise<void> => {
    const r = await fetch(`/api/me/body/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (!r.ok) throw new ApiError(`/api/me/body/${id}`, r.status);
  },

  /** El atleta envía un mensaje al coach. */
  sendCoachMessage: async (body: string): Promise<{ message: import("./lib/types").DirectMessage }> => {
    const res = await fetch("/api/me/coach-messages", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body }),
    });
    if (!res.ok) throw new ApiError("/api/me/coach-messages", res.status);
    return res.json() as Promise<{ message: import("./lib/types").DirectMessage }>;
  },

  /** Valida un código de invitación. Si es válido, el backend deja la cookie para ir a Strava. */
  redeemCode: async (code: string): Promise<{ ok: boolean; accountType?: string; coachCode?: boolean; coachName?: string; error?: string }> => {
    const res = await fetch("/auth/redeem", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    });
    return (await res.json().catch(() => ({ ok: false }))) as {
      ok: boolean;
      accountType?: string;
      coachCode?: boolean;
      coachName?: string;
      error?: string;
    };
  },

  logout: async (): Promise<void> => {
    await fetch("/auth/logout", { method: "POST" });
  },

  identity: () =>
    getJson<{ email: string | null; methods: string[] }>("/api/me/identity"),

  connections: () =>
    getJson<{
      connected: { provider: string; scope: string | null; connectedAt: string | null; lastSyncAt: string | null }[];
      catalog: { provider: string; displayName: string; capabilities: string[]; status: string; brandColor: string; isConnected: boolean; tos: { thirdPartyDisplay: "forbidden" | "consent_required"; aiModelTraining: boolean } }[];
    }>("/api/me/connections"),

  disconnectProvider: async (provider: string): Promise<{ ok: boolean; deauthorized: boolean; error?: string }> => {
    const res = await fetch(`/api/me/connections/${encodeURIComponent(provider)}`, { method: "DELETE" });
    return (await res.json().catch(() => ({ ok: false, deauthorized: false }))) as {
      ok: boolean;
      deauthorized: boolean;
      error?: string;
    };
  },

  setSourcePrefs: async (prefs: { sportPrimary?: string }): Promise<void> => {
    const res = await fetch("/api/me/source-prefs", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(prefs),
    });
    if (!res.ok) throw new ApiError("/api/me/source-prefs", res.status);
  },
};

/** Cliente del panel de admin (/api/admin). Solo funciona si el usuario es admin. */
export const adminApi = {
  users: () =>
    getJson<{ users: import("./lib/types").AdminUser[]; slots: import("./lib/types").ProviderSlot[] }>(
      "/api/admin/users",
    ),

  setAccess: async (id: string, status: "active" | "blocked"): Promise<void> => {
    const res = await fetch(`/api/admin/users/${id}/access`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new ApiError(`/api/admin/users/${id}/access`, res.status);
  },

  setAccountType: async (id: string, accountType: "athlete" | "coach"): Promise<void> => {
    const res = await fetch(`/api/admin/users/${id}/account-type`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountType }),
    });
    if (!res.ok) throw new ApiError(`/api/admin/users/${id}/account-type`, res.status);
  },

  revoke: async (id: string): Promise<{ ok: boolean; deauthorized: boolean; hadToken: boolean }> => {
    const res = await fetch(`/api/admin/users/${id}/revoke`, { method: "POST" });
    if (!res.ok) throw new ApiError(`/api/admin/users/${id}/revoke`, res.status);
    return (await res.json()) as { ok: boolean; deauthorized: boolean; hadToken: boolean };
  },

  deleteUser: async (id: string): Promise<{ ok: boolean; deauthorized: boolean; hadToken: boolean }> => {
    const res = await fetch(`/api/admin/users/${id}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/admin/users/${id}`, res.status);
    return (await res.json()) as { ok: boolean; deauthorized: boolean; hadToken: boolean };
  },

  aiUsage: () => getJson<import("./lib/types").AiUsageSummary>("/api/admin/ai-usage"),

  // Compat: mantiene /codes para el historial; las nuevas rutas usan invite-codes.
  codes: () => getJson<{ codes: import("./lib/types").AdminCode[] }>("/api/admin/codes"),

  inviteCodes: () =>
    getJson<{ codes: import("./lib/types").InviteCode[] }>("/api/admin/invite-codes"),

  inviteRedemptions: (code: string) =>
    getJson<{ redemptions: import("./lib/types").InviteRedemption[] }>(
      `/api/admin/invite-codes/${encodeURIComponent(code)}/redemptions`,
    ),

  createInviteCode: async (opts: {
    kind: "beta" | "coach_link";
    prefix: string;
    accountType?: "athlete" | "coach";
    coachUserId?: string;
    note?: string;
    expiresAt?: string;
    maxUses?: number | null;
  }): Promise<{ ok: boolean; code: import("./lib/types").InviteCode }> => {
    const res = await fetch("/api/admin/invite-codes", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(opts),
    });
    if (!res.ok) throw new ApiError("/api/admin/invite-codes", res.status);
    return (await res.json()) as { ok: boolean; code: import("./lib/types").InviteCode };
  },

  setInviteCodeStatus: async (code: string, status: "active" | "disabled"): Promise<void> => {
    const res = await fetch(`/api/admin/invite-codes/${encodeURIComponent(code)}/status`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new ApiError(`/api/admin/invite-codes/${code}/status`, res.status);
  },

  deleteInviteCode: async (code: string): Promise<void> => {
    const res = await fetch(`/api/admin/invite-codes/${encodeURIComponent(code)}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/admin/invite-codes/${code}`, res.status);
  },

  createCode: async (
    accountType: "athlete" | "coach",
    _prefix: string,
    note: string,
  ): Promise<{ ok: boolean; code: import("./lib/types").AdminCode }> => {
    const res = await fetch("/api/admin/invite-codes", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountType, note, maxUses: 1 }),
    });
    if (!res.ok) throw new ApiError("/api/admin/invite-codes", res.status);
    return (await res.json()) as { ok: boolean; code: import("./lib/types").AdminCode };
  },

  setCodeStatus: async (code: string, status: "active" | "disabled"): Promise<void> => {
    const res = await fetch(`/api/admin/invite-codes/${encodeURIComponent(code)}/status`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new ApiError(`/api/admin/invite-codes/${code}/status`, res.status);
  },

  mergeUsers: async (fromUserId: string, toUserId: string): Promise<{ ok: boolean; error?: string }> => {
    const res = await fetch("/api/admin/users/merge", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ fromUserId, toUserId }),
    });
    const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
    return { ok: res.ok && body.ok !== false, error: body.error };
  },

  setAdmin: async (userId: string, isAdmin: boolean): Promise<void> => {
    await fetch(`/api/admin/users/${encodeURIComponent(userId)}/admin`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ isAdmin }),
    });
  },
};

export const GOOGLE_LOGIN_URL = "/auth/google";

/** Registro con email+contraseña (F1). `code` es fallback cuando la cookie de invitación falta. */
export const registerEmail = async (
  email: string, password: string, code?: string,
): Promise<{ ok: boolean; needsConnection?: boolean; error?: string }> => {
  const res = await fetch("/auth/email/register", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password, ...(code ? { code } : {}) }),
    credentials: "include",
  });
  const data = (await res.json().catch(() => ({ ok: false, error: "registro_fallido" }))) as { ok: boolean; needsConnection?: boolean; error?: string };
  // 500 con cuerpo de error válido → devolver el error mapeado en lugar de lanzar.
  if (!res.ok && res.status !== 409 && res.status !== 403 && res.status !== 400 && res.status !== 500) {
    throw new ApiError("/auth/email/register", res.status);
  }
  return data;
};

/** Login con email+contraseña (F1). */
export const loginEmail = async (
  email: string, password: string,
): Promise<{ ok: boolean; error?: string }> => {
  const res = await fetch("/auth/email/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
    credentials: "include",
  });
  const data = (await res.json()) as { ok: boolean; error?: string };
  if (!res.ok && res.status !== 401 && res.status !== 403 && res.status !== 400) {
    throw new ApiError("/auth/email/login", res.status);
  }
  return data;
};

/** Cliente de las rutas de entrenador (/api/coach). Solo funciona si el usuario es coach. */
export const coachApi = {
  me: () =>
    getJson<{
      id: string;
      firstname: string | null;
      lastname: string | null;
      profilePic: string | null;
      account_type: string;
      athleteCount: number;
    }>("/api/coach/me"),

  athletes: () =>
    getJson<{ athletes: import("./lib/types").CoachRosterEntry[] }>("/api/coach/athletes"),

  conversations: () =>
    getJson<{ conversations: import("./lib/types").CoachConversation[] }>("/api/coach/conversations"),

  athleteDashboard: (athleteId: string) =>
    getJson<import("./lib/types").DashboardData>(`/api/coach/athletes/${athleteId}/dashboard`),

  /** Entrenos del atleta en un rango (incluye borradores; para el coach). */
  calendar: (athleteId: string, from: string, to: string) =>
    getJson<{ workouts: import("./lib/types").PlannedWorkout[] }>(
      `/api/coach/athletes/${athleteId}/calendar?from=${from}&to=${to}`,
    ),

  /** Actividades reales del atleta en un rango (para el calendario del coach). */
  activities: (athleteId: string, from: string, to: string) =>
    getJson<{ activities: import("./lib/types").ActivitySummary[] }>(
      `/api/coach/athletes/${athleteId}/activities?from=${from}&to=${to}`,
    ),

  /** Crea un entreno en el calendario del atleta. */
  createWorkout: async (
    athleteId: string,
    data: { scheduledDate: string; payload: import("./lib/types").WorkoutPayload; status?: "draft" | "published" },
  ): Promise<{ workout: import("./lib/types").PlannedWorkout }> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/workouts`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/workouts`, res.status);
    return (await res.json()) as { workout: import("./lib/types").PlannedWorkout };
  },

  /** Edita un entreno existente. */
  updateWorkout: async (
    athleteId: string,
    wid: string,
    data: { scheduledDate?: string; payload?: import("./lib/types").WorkoutPayload; status?: string },
  ): Promise<void> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/workouts/${wid}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/workouts/${wid}`, res.status);
  },

  /** Elimina un entreno. */
  deleteWorkout: async (athleteId: string, wid: string): Promise<void> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/workouts/${wid}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/workouts/${wid}`, res.status);
  },

  /** Envía un mensaje al Coach IA en modo planificación (contexto del atleta, crédito del coach). */
  planChat: async (
    athleteId: string,
    data: { message: string; lang: string; threadId?: string | null },
  ): Promise<{ threadId: string; reply: string; source: "ai" | "rules"; usage: import("./api").CoachUsage }> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/coach`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/coach`, res.status);
    return res.json() as Promise<{ threadId: string; reply: string; source: "ai" | "rules"; usage: import("./api").CoachUsage }>;
  },

  /** Lista notas del coach sobre el atleta (todas: private + shared). */
  notes: async (athleteId: string): Promise<{ notes: import("./lib/types").CoachNote[] }> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/notes`);
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/notes`, res.status);
    return res.json() as Promise<{ notes: import("./lib/types").CoachNote[] }>;
  },

  /** Crea una nota. */
  createNote: async (
    athleteId: string,
    data: { note: string; visibility: "private" | "shared" },
  ): Promise<{ note: import("./lib/types").CoachNote }> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/notes`, res.status);
    return res.json() as Promise<{ note: import("./lib/types").CoachNote }>;
  },

  /** Edita una nota. */
  updateNote: async (
    athleteId: string,
    nid: string,
    data: { note?: string; visibility?: "private" | "shared" },
  ): Promise<void> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/notes/${nid}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/notes/${nid}`, res.status);
  },

  /** Elimina una nota. */
  deleteNote: async (athleteId: string, nid: string): Promise<void> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/notes/${nid}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/notes/${nid}`, res.status);
  },

  /** El coach revoca el vínculo con un atleta. */
  revokeAthlete: async (athleteId: string): Promise<void> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}`, { method: "DELETE" });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}`, res.status);
  },

  /** Lista los códigos de vínculo del coach (desde invite_codes). */
  linkCodes: () =>
    getJson<{ codes: import("./lib/types").InviteCode[] }>("/api/coach/link-codes"),

  /** Crea un código de vínculo nuevo. */
  createLinkCode: async (_data: Record<string, never> = {}): Promise<{ code: import("./lib/types").InviteCode }> => {
    const res = await fetch("/api/coach/link-codes", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new ApiError("/api/coach/link-codes", res.status);
    return res.json() as Promise<{ code: import("./lib/types").InviteCode }>;
  },

  /** Activa o deshabilita un código de vínculo. */
  setLinkCodeStatus: async (code: string, status: "active" | "disabled"): Promise<void> => {
    const res = await fetch(`/api/coach/link-codes/${code}/status`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new ApiError(`/api/coach/link-codes/${code}/status`, res.status);
  },

  /** Lista los mensajes del hilo con un atleta (y los marca como leídos). */
  messages: (athleteId: string) =>
    getJson<{ messages: import("./lib/types").DirectMessage[] }>(
      `/api/coach/athletes/${athleteId}/messages`,
    ),

  /** El coach envía un mensaje a un atleta. */
  sendMessage: async (
    athleteId: string,
    body: string,
  ): Promise<{ message: import("./lib/types").DirectMessage }> => {
    const res = await fetch(`/api/coach/athletes/${athleteId}/messages`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body }),
    });
    if (!res.ok) throw new ApiError(`/api/coach/athletes/${athleteId}/messages`, res.status);
    return res.json() as Promise<{ message: import("./lib/types").DirectMessage }>;
  },
};
